/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import { useEffect, useState, useCallback } from 'react';

/**
 * Custom hook to handle portfolio changes across OEM admin components
 * This hook detects when the portfolio ID changes in sessionStorage and
 * triggers a callback function to refetch data
 */
export const usePortfolioChange = (callback, dependencies = []) => {
  const [currentPortfolioId, setCurrentPortfolioId] = useState(
    sessionStorage.getItem("portfolioId")
  );
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Stable callback reference
  const stableCallback = useCallback(callback, [callback, ...dependencies]);

  // Monitor portfolio ID changes from sessionStorage
  useEffect(() => {
    const handleStorageChange = (event) => {
      // Handle both custom storage events and natural storage changes
      const newPortfolioId = event?.key === 'portfolioId' 
        ? event.newValue 
        : sessionStorage.getItem("portfolioId");
        
      if (newPortfolioId !== currentPortfolioId) {
        setCurrentPortfolioId(newPortfolioId);
        // Only trigger the callback for actual portfolio changes, not initial mount
        if (stableCallback && !isInitialMount) {
          stableCallback(newPortfolioId);
        }
      }
    };

    // Enhanced storage check function
    const checkStorageChange = () => {
      const newPortfolioId = sessionStorage.getItem("portfolioId");
      if (newPortfolioId !== currentPortfolioId) {
        setCurrentPortfolioId(newPortfolioId);
        // Only trigger the callback for actual portfolio changes, not initial mount
        if (stableCallback && !isInitialMount) {
          stableCallback(newPortfolioId);
        }
      }
    };

    // Listen for storage changes (including manual events)
    globalThis.addEventListener("storage", handleStorageChange);
    
    // Also check on component focus (when navigating back to this page)
    window.addEventListener("focus", checkStorageChange);
    
    // Check for changes on window focus and visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkStorageChange();
      }
    };
    
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", checkStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [currentPortfolioId, stableCallback, isInitialMount]);

  // Effect to mark initial mount as complete after the first render
  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
    }
  }, [isInitialMount]);

  return currentPortfolioId;
};

